<script setup lang="ts">
import { onMounted, onBeforeUnmount, useTemplateRef, watch } from 'vue';
import type { SpriteRegion } from './login-platform';

const props = defineProps<{ target: SpriteRegion | null; orbit: SpriteRegion | null; exclusions: SpriteRegion[] }>();
const canvas = useTemplateRef<HTMLCanvasElement>('canvas');
const orbitRadii = [0.176, 0.313, 0.45];
const particles = Array.from({ length: 360 }, (_, index) => ({
  x: Math.random(),
  y: Math.random(),
  angle: Math.random() * Math.PI * 2,
  ring: index % 3,
  onOrbit: index % 2 === 0,
  spread: (Math.random() - 0.5) * 0.05,
  size: 0.5 + Math.random() * 1.1,
  phase: Math.random() * Math.PI * 2,
  orbitSpeed: 0.008 + Math.random() * 0.01,
  driftX: 2 + Math.random() * 4,
  driftY: 0.6 + Math.random() * 1.2,
  color: index % 10 < 3 ? 3 : index % 10 < 5 ? 4 : index % 13 === 0 ? 1 : index % 5 === 0 ? 2 : 0,
}));
// Hover adds a quiet local scatter without changing the background stars' drift.
const accentStars = Array.from({ length: 72 }, (_, index) => ({
  x: (Math.random() + Math.random() + Math.random()) / 3 - 0.5,
  y: (Math.random() + Math.random() + Math.random()) / 3 - 0.5,
  size: 0.5 + Math.random() * 1.3,
  phase: Math.random() * Math.PI * 2,
  color: index % 3 === 0 ? 0 : index % 3 === 1 ? 3 : 4,
}));
let context: CanvasRenderingContext2D | null = null;
let sprites: HTMLCanvasElement[] = [];
let width = 0;
let height = 0;
let particleCount = particles.length;
let frame = 0;
let lastTime = 0;
let elapsed = 0;
let visible = true;
let highlightStrength = 0;
let highlightRegion: SpriteRegion | null = null;
let reducedMotion: MediaQueryList | null = null;
let resizeObserver: ResizeObserver | null = null;
let intersectionObserver: IntersectionObserver | null = null;

function makeSprite(color: string, index: number) {
  const texture = document.createElement('canvas');
  texture.width = texture.height = 64;
  const textureContext = texture.getContext('2d')!;
  const glow = textureContext.createRadialGradient(32, 32, 0, 32, 32, 32);
  const core = index >= 3 ? color : '#ffffff';
  glow.addColorStop(0, core);
  glow.addColorStop(0.17, core);
  glow.addColorStop(0.28, color);
  glow.addColorStop(0.56, color + '88');
  glow.addColorStop(1, color + '00');
  textureContext.fillStyle = glow;
  textureContext.fillRect(0, 0, 64, 64);
  return texture;
}

function drawStar(x: number, y: number, size: number, color: number, alpha: number) {
  if (!context) return;
  let clearance = 1;
  for (const region of props.exclusions) {
    const dx = Math.max(region.x - x, 0, x - region.x - region.width) * width;
    const dy = Math.max(region.y - y, 0, y - region.y - region.height) * height;
    clearance = Math.min(clearance, Math.hypot(dx, dy) / 16);
  }
  if (!clearance) return;
  context.globalAlpha = alpha * clearance;
  const diameter = size * 6;
  context.drawImage(sprites[color], x * width - diameter / 2, y * height - diameter / 2, diameter, diameter);
}

function render(time: number) {
  if (!context || !width || !height) return;
  const delta = lastTime ? Math.min((time - lastTime) / 1000, 0.05) : 1 / 60;
  lastTime = time;
  if (!reducedMotion?.matches) elapsed += delta;
  highlightStrength +=
    ((props.target ? 1 : 0) - highlightStrength) * (reducedMotion?.matches ? 1 : 1 - Math.exp(-delta * 3));
  context.clearRect(0, 0, width, height);
  context.globalCompositeOperation = 'lighter';

  for (let index = 0; index < particleCount; index++) {
    const particle = particles[index];
    let x = (((particle.x * (width + 40) + elapsed * particle.driftX) % (width + 40)) - 20) / width;
    let y = (((particle.y * (height + 40) + elapsed * particle.driftY) % (height + 40)) - 20) / height;
    if (props.orbit && particle.onOrbit) {
      const radius = orbitRadii[particle.ring] + particle.spread;
      const angle = particle.angle + elapsed * particle.orbitSpeed;
      x = props.orbit.x + (0.484 + Math.cos(angle) * radius) * props.orbit.width;
      y = props.orbit.y + (0.545 + Math.sin(angle) * radius * 0.94) * props.orbit.height;
    }
    const twinkle = 0.82 + Math.sin(elapsed * 0.8 + particle.phase) * 0.18;
    drawStar(x, y, particle.size, particle.color, twinkle * 0.6);
  }
  if (highlightRegion && highlightStrength > 0.005) {
    const region = highlightRegion;
    for (const star of accentStars) {
      const x =
        region.x +
        region.width / 2 +
        star.x * (region.width * 1.7 + 50 / width) +
        (Math.sin(elapsed * 0.3 + star.phase) * 8) / width;
      const y =
        region.y +
        region.height / 2 +
        star.y * (region.height * 1.7 + 50 / height) +
        (Math.cos(elapsed * 0.25 + star.phase) * 6) / height;
      const twinkle = 0.82 + Math.sin(elapsed * 0.8 + star.phase) * 0.18;
      drawStar(x, y, star.size, star.color, twinkle * highlightStrength * 0.45);
    }
  }
  context.globalAlpha = 1;
}

function animate(time: number) {
  frame = 0;
  render(time);
  if (visible && !document.hidden && !reducedMotion?.matches) frame = requestAnimationFrame(animate);
}
function restart() {
  cancelAnimationFrame(frame);
  frame = 0;
  lastTime = 0;
  if (visible && !document.hidden && width && height) {
    render(performance.now());
    if (!reducedMotion?.matches) frame = requestAnimationFrame(animate);
  }
}
function resize() {
  if (!canvas.value || !context) return;
  const rect = canvas.value.getBoundingClientRect();
  width = rect.width;
  height = rect.height;
  particleCount = Math.min(particles.length, Math.max(70, Math.round((width * height) / 2600)));
  const scale = Math.min(window.devicePixelRatio || 1, 2);
  canvas.value.width = Math.round(width * scale);
  canvas.value.height = Math.round(height * scale);
  context.setTransform(scale, 0, 0, scale, 0, 0);
  restart();
}
watch(
  () => props.target,
  (target) => {
    if (target) {
      highlightRegion = target;
      highlightStrength = 0;
    }
    if (reducedMotion?.matches) render(performance.now());
  },
);
watch(
  () => [props.orbit, props.exclusions],
  () => {
    if (reducedMotion?.matches) render(performance.now());
  },
);
onMounted(() => {
  if (!canvas.value) return;
  context = canvas.value.getContext('2d');
  if (!context) return;
  sprites = ['#d9edff', '#ffce70', '#81c7ff', '#3153a8', '#547aca'].map(makeSprite);
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  reducedMotion.addEventListener('change', restart);
  resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(canvas.value);
  intersectionObserver = new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
    restart();
  });
  intersectionObserver.observe(canvas.value);
  document.addEventListener('visibilitychange', restart);
  resize();
});
onBeforeUnmount(() => {
  cancelAnimationFrame(frame);
  resizeObserver?.disconnect();
  intersectionObserver?.disconnect();
  reducedMotion?.removeEventListener('change', restart);
  document.removeEventListener('visibilitychange', restart);
});
</script>

<template>
  <canvas ref="canvas" class="orbit-sprites" aria-hidden="true"></canvas>
</template>

<style scoped>
.orbit-sprites {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  animation: stars-arrive 1.8s ease-out 200ms backwards;
}
@keyframes stars-arrive {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
@media (prefers-reduced-motion: reduce) {
  .orbit-sprites {
    animation: none;
  }
}
</style>
